/**
 * ScriptMatrixStep — the open body of a multi-code script card (BV-2742).
 *
 * One card per topic instead of one per code: each authored question is a row header
 * over the fields that answer it, and every code on the case is a column, so the rep
 * reads a question once and fills it for every code on the same line. Built from the
 * unit's {@link ScriptMatrix} (see `buildScriptModel`'s `matrix` layout).
 *
 * Per row:
 *   - the field label (sticky while the grid scrolls sideways) with the required dot,
 *     and — on a live call — a Heard toggle for every code, showing how many are heard
 *     ("2/5") and ✓ once all are;
 *   - "Same for all": a choice field gets an "All…" dropdown and anything else a
 *     "Copy first → all" button. Either OVERWRITES every code the field applies to, as
 *     one output revision. Shown only when two or more codes take the field;
 *   - one cell per code: the same widget, Accept chip and evidence a FieldRow has
 *     (ScriptFieldParts), or "n/a · <reason>" when the field cannot apply to that code.
 *
 * The All / Missed / Required filter narrows `unit.fields`; a row shows when any of its
 * cells survived, but always with every cell, so the columns stay aligned.
 */
import { FormFieldRenderer } from '@components/forms/FormFieldRenderer';
import { Markdown } from '@components/Markdown';
import { Check, Ear } from 'lucide-react';
import React from 'react';

import { cn } from '@/utils';
import {
  isResolvedFieldRequired,
  type ResolvedField,
  type ScriptMatrixInstance,
  type ScriptMatrixRow,
  type ScriptUnit,
} from '@/utils/humanCallModel';

import { isHeardEligible } from './heard';
import { FieldControl, fieldEvidenceCluster } from './ScriptFieldParts';
import { centerInMatrixScroller, centerInScrollParent, flashReveal } from './scrollInPane';

interface ScriptMatrixStepProps {
  unit: ScriptUnit;
  readOnly: boolean;
  /** True only while the call is live. Gates the Heard buttons. */
  callActive: boolean;
  onFieldChange: (key: string, value: unknown) => void;
  /** Writes several fields as ONE output revision — what "Same for all" uses. */
  onFieldsChange: (entries: ReadonlyArray<{ key: string; value: unknown }>) => void;
  onFieldCommit: (key?: string) => void;
  onToggleHeard: (key: string) => void;
  caseRecordId?: string;
  outputMetaFields?: Record<string, unknown>;
  extractedMetaFields?: Record<string, unknown>;
  extractedData?: Record<string, unknown>;
  onNavigateToCall?: (callId: string, evidenceTimeStart?: string, evidenceTimeEnd?: string) => void;
  onNavigateToAudit?: (auditId: string) => void;
  fieldErrors?: Record<string, string>;
  /** Field to bring into view; the matching cell scrolls itself there once rendered. */
  revealKey?: string | null;
  onRevealed?: () => void;
}

type CellHandlers = Omit<ScriptMatrixStepProps, 'unit' | 'onFieldsChange'>;

const isEmptyValue = (value: unknown) =>
  value === undefined ||
  value === null ||
  value === '' ||
  (Array.isArray(value) && value.length === 0);

/** The cells a row-level action may touch: present and applicable to their code. */
function applicableCells(row: ScriptMatrixRow): ResolvedField[] {
  return row.cells.filter((c): c is ResolvedField => c !== null && !c.notApplicable);
}

/**
 * A cell the Heard toggle works on: heard-eligible and either still empty or already
 * marked heard. A captured value needs no recording, so it drops out.
 */
function isHeardToggleable(cell: ResolvedField): boolean {
  return (
    (cell.status === 'missing' || cell.status === 'optional' || cell.status === 'heard') &&
    isHeardEligible(cell.field.type)
  );
}

const heardButtonClass = (on: boolean) =>
  cn(
    'inline-flex shrink-0 items-center gap-1 rounded-md border text-[11px] font-semibold',
    on
      ? 'border-primary bg-primary/15 text-primary hover:bg-primary/25'
      : 'border-attention-border bg-attention/40 text-attention-foreground hover:bg-attention/60',
  );

/**
 * The row-level Heard toggle: ear when no code is heard, "ear n/m" when some are, ✓ when
 * all are. A click marks the codes not yet heard, or — once every code is — unmarks all.
 */
const RowHeardToggle: React.FC<{
  label: string;
  cells: ResolvedField[];
  onToggleHeard: (key: string) => void;
}> = ({ label, cells, onToggleHeard }) => {
  const heard = cells.filter(c => c.status === 'heard').length;
  const all = heard === cells.length;
  return (
    <button
      type="button"
      onClick={() =>
        (all ? cells : cells.filter(c => c.status !== 'heard')).forEach(c => onToggleHeard(c.key))
      }
      className={cn('ml-auto px-1.5 py-1', heardButtonClass(all))}
      aria-pressed={all}
      title={
        all
          ? 'Heard for every code · click to unmark all'
          : 'Mark heard for every code — recover the values from the recording after the call'
      }
      aria-label={
        all ? `Unmark ${label} heard for every code` : `Mark ${label} heard for every code`
      }
    >
      {all ? (
        <Check className="h-3.5 w-3.5" aria-hidden="true" />
      ) : (
        <Ear className="h-3.5 w-3.5" aria-hidden="true" />
      )}
      {heard > 0 && !all && (
        <span>
          {heard}/{cells.length}
        </span>
      )}
    </button>
  );
};

/** One code's cell: the field widget, or "n/a · reason". Scrolls itself into view on reveal. */
const MatrixCell: React.FC<{ cell: ResolvedField; code: string } & CellHandlers> = ({
  cell,
  code,
  readOnly,
  callActive,
  onFieldChange,
  onFieldCommit,
  onToggleHeard,
  caseRecordId,
  outputMetaFields,
  extractedMetaFields,
  extractedData,
  onNavigateToCall,
  onNavigateToAudit,
  fieldErrors,
  revealKey,
  onRevealed,
}) => {
  // Same self-scrolling contract as FieldRow: the cell may only mount after its card
  // opens, so it scrolls itself — down the column, then across the grid to its code.
  const cellRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (revealKey !== cell.key || !cellRef.current) return;
    centerInScrollParent(cellRef.current);
    centerInMatrixScroller(cellRef.current);
    flashReveal(cellRef.current);
    onRevealed?.();
  }, [revealKey, cell.key, onRevealed]);

  if (cell.notApplicable) {
    return (
      <div
        ref={cellRef}
        data-field-id={cell.key}
        className="truncate rounded-md bg-muted px-2 py-1.5 text-[11px] text-muted-foreground"
        title={`Not applicable to ${code}: ${cell.notApplicable}`}
      >
        n/a · {cell.notApplicable}
      </div>
    );
  }

  const evidence = fieldEvidenceCluster({
    field: cell,
    outputMetaFields,
    extractedMetaFields,
    onNavigateToCall,
    onNavigateToAudit,
  });
  const showHeard = callActive && evidence === null && isHeardToggleable(cell);
  const heard = cell.status === 'heard';

  return (
    // data-field-id: the hook scrollToFirstOutputError and the board's reveal use.
    <div ref={cellRef} data-field-id={cell.key} className="flex items-center gap-1 rounded-md">
      {/* A heard cell outlines its widget, so a glance down the column shows which codes
          were heard and are waiting on the recording. */}
      <div className={cn('min-w-0 flex-1 rounded-md', heard && 'ring-1 ring-primary')}>
        <FieldControl
          field={cell}
          readOnly={readOnly}
          extractedData={extractedData}
          onFieldChange={onFieldChange}
          onFieldCommit={onFieldCommit}
          caseRecordId={caseRecordId}
          fieldErrors={fieldErrors}
          dense
        />
      </div>
      {evidence !== null && <div className="flex shrink-0 items-center gap-0.5">{evidence}</div>}
      {/* Stays after a click, showing ✓, so a mistaken mark can be undone. */}
      {showHeard && (
        <button
          type="button"
          onClick={() => onToggleHeard(cell.key)}
          className={cn('p-1.5', heardButtonClass(heard))}
          aria-pressed={heard}
          title={
            heard
              ? `Heard for ${code} · click to unmark`
              : `Heard for ${code} — recover the value from the recording after the call`
          }
          aria-label={
            heard
              ? `Unmark ${cell.field.label} heard for ${code}`
              : `Mark ${cell.field.label} heard for ${code}`
          }
        >
          {heard ? (
            <Check className="h-3.5 w-3.5" aria-hidden="true" />
          ) : (
            <Ear className="h-3.5 w-3.5" aria-hidden="true" />
          )}
        </button>
      )}
    </div>
  );
};

/**
 * "Same for all" for one row. Writes OVERWRITE every applicable code, including ones
 * that already hold a value, as a single output revision.
 */
const SameForAll: React.FC<{
  row: ScriptMatrixRow;
  instances: ScriptMatrixInstance[];
  controlId: string;
  onFieldsChange: ScriptMatrixStepProps['onFieldsChange'];
  fieldErrors?: Record<string, string>;
}> = ({ row, instances, controlId, onFieldsChange, fieldErrors }) => {
  const cells = applicableCells(row);
  if (cells.length < 2) return null;

  if (row.field.type === 'select' && (row.field.options?.length ?? 0) > 0) {
    return (
      <FormFieldRenderer
        // Always empty: it is an action, not a value. A shared value already shows in
        // every cell, and SearchableSelect clears on re-picking its current option.
        field={{ ...row.field, id: controlId, placeholder: 'All…', disabled: false }}
        value=""
        onChange={value => {
          if (isEmptyValue(value)) return;
          onFieldsChange(cells.map(c => ({ key: c.key, value })));
        }}
        hideLabel
        compact
      />
    );
  }

  // The first code that has a usable value. One the rep left invalid is skipped, so a
  // malformed number is never copied onto every other code.
  const sourceIndex = row.cells.findIndex(
    c => c !== null && !c.notApplicable && !isEmptyValue(c.value) && !fieldErrors?.[c.key],
  );
  const source = sourceIndex >= 0 ? row.cells[sourceIndex] : null;
  const sourceCode = sourceIndex >= 0 ? instances[sourceIndex]?.code : undefined;
  return (
    <button
      type="button"
      disabled={!source}
      onClick={() => {
        if (!source) return;
        onFieldsChange(
          cells.filter(c => c.key !== source.key).map(c => ({ key: c.key, value: source.value })),
        );
      }}
      className="w-full whitespace-nowrap rounded-md border border-dashed border-border px-2 py-1.5 text-[12px] text-muted-foreground hover:bg-muted/40 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
      title={
        sourceCode
          ? `Copy ${sourceCode}'s value to every other code`
          : 'Fill one code first, then copy it to the rest'
      }
    >
      Copy first → all
    </button>
  );
};

/** The open body of a matrix card: guidance once, then each question over its grid rows. */
export const ScriptMatrixStep: React.FC<ScriptMatrixStepProps> = ({
  unit,
  onFieldsChange,
  ...handlers
}) => {
  const matrix = unit.matrix;
  if (!matrix) return null;
  const { instances } = matrix;
  const { readOnly, callActive, onToggleHeard, fieldErrors } = handlers;

  // The filter narrowed `unit.fields`; a row survives when any of its cells did.
  const visibleKeys = new Set(unit.fields.map(f => f.key));
  const questions = matrix.questions
    .map(q => ({
      ...q,
      rows: q.rows.filter(r => r.cells.some(c => c !== null && visibleKeys.has(c.key))),
    }))
    .filter(q => q.rows.length > 0);

  // Label + "Same for all" + one column per code.
  const colSpan = instances.length + 2;

  return (
    <div className="space-y-3 pb-3.5 pl-12 pr-4">
      {unit.helpText && (
        <div className="ml-0.5 text-[12px] leading-relaxed text-muted-foreground">
          <Markdown>{unit.helpText}</Markdown>
        </div>
      )}
      {/* The grid's own sideways scroller. centerInMatrixScroller looks for this
          attribute, so a reveal moves only the grid, never the Review row. */}
      <div className="overflow-x-auto" data-matrix-scroller>
        <table className="min-w-full border-separate border-spacing-x-0 border-spacing-y-1">
          <tbody>
            {questions.map((question, qi) => (
              <React.Fragment key={question.questionId}>
                <tr>
                  <td colSpan={colSpan} className="pt-2">
                    {/* Sticky so the question stays readable while the codes scroll. */}
                    <div className="sticky left-0 inline-flex max-w-[640px] items-start gap-2 rounded-r-lg border-l-[3px] border-primary/40 bg-primary/5 px-3 py-2 text-[13px] leading-relaxed text-foreground">
                      <span className="mt-0.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-primary/10 text-[10px] font-bold text-primary">
                        {qi + 1}
                      </span>
                      <div className="min-w-0">
                        <Markdown>{question.prompt}</Markdown>
                      </div>
                    </div>
                  </td>
                </tr>
                <tr className="text-[11px] text-muted-foreground">
                  <th className="sticky left-0 z-[1] min-w-[170px] bg-card px-1.5 text-left font-normal">
                    Field
                  </th>
                  <th className="min-w-[130px] px-1.5 text-left font-normal">Same for all</th>
                  {instances.map(instance => (
                    <th key={instance.index} className="min-w-[160px] px-1.5 text-left">
                      <span className="block text-[12px] font-bold text-foreground">
                        {instance.code}
                      </span>
                      {instance.name && <span className="block font-normal">{instance.name}</span>}
                    </th>
                  ))}
                </tr>
                {question.rows.map(row => {
                  const required = row.cells.some(c => c !== null && isResolvedFieldRequired(c));
                  const heardTargets = callActive
                    ? applicableCells(row).filter(isHeardToggleable)
                    : [];
                  return (
                    <tr key={row.rowKey}>
                      <td className="sticky left-0 z-[1] bg-card px-1.5 align-middle">
                        <div className="flex items-center gap-1.5 text-[12.5px] font-semibold text-foreground">
                          {/* Same quiet required dot as FieldRow (BI-1467). */}
                          <span
                            className={cn(
                              'h-1.5 w-1.5 shrink-0 rounded-full',
                              required ? 'bg-destructive' : 'bg-transparent',
                            )}
                            aria-hidden="true"
                          />
                          <span className="min-w-0">
                            {row.field.label}
                            {required && <span className="sr-only"> (required)</span>}
                          </span>
                          {heardTargets.length > 1 && (
                            <RowHeardToggle
                              label={row.field.label}
                              cells={heardTargets}
                              onToggleHeard={onToggleHeard}
                            />
                          )}
                        </div>
                      </td>
                      <td className="px-1.5 align-middle">
                        {!readOnly && (
                          <SameForAll
                            row={row}
                            instances={instances}
                            controlId={`same-for-all-${unit.id}-${row.rowKey}`}
                            onFieldsChange={onFieldsChange}
                            fieldErrors={fieldErrors}
                          />
                        )}
                      </td>
                      {row.cells.map((cell, i) => (
                        <td key={instances[i]?.index ?? i} className="px-1.5 align-middle">
                          {cell && (
                            <MatrixCell cell={cell} code={instances[i]?.code ?? ''} {...handlers} />
                          )}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
